import { useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useCallback, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  MediaItem,
  message,
} from 'app/pages/request-single-page/components-v4';
import {
  attachFilesToPublicRequest,
  reserveAttachments,
  uploadAttachment,
} from 'app/pages/request-single-page/request-single-page-slice';
import { getPublicId } from 'app/pages/request-single-page/selectors';

type UploadStatus =
  | 'idle'
  | 'reserving'
  | 'uploading'
  | 'attaching'
  | 'done'
  | 'error';

type FileUploadResult = {
  succeeded: MediaItem[];
  failed: MediaItem[];
};

export const useFilesUpload = () => {
  const dispatch = useAppDispatch();
  const publicId = useSelector(getPublicId);

  const [status, setStatus] = useState<UploadStatus>('idle');

  const upload = useCallback(
    async (files: MediaItem[]): Promise<FileUploadResult> => {
      setStatus('reserving');

      let reservations: {
        filename: string;
        file_id: string;
        shared_id: string;
      }[];

      try {
        reservations = await dispatch(
          reserveAttachments(files.map((file) => file.name))
        ).then(unwrapResult);
      } catch {
        setStatus('error');
        message({
          type: 'error',
          text: 'Не удалось зарезервировать файлы',
        });

        return { succeeded: [], failed: files };
      }

      setStatus('uploading');

      const succeeded: MediaItem[] = [];
      const failed: MediaItem[] = [];
      const succeededFiles: { file_id: string; filename: string }[] = [];

      for (const file of files) {
        const reservation = reservations.find((r) => r.filename === file.name);

        if (!reservation || !file.file) {
          failed.push(file);
          continue;
        }

        try {
          await dispatch(
            uploadAttachment({
              shared_id: reservation.shared_id,
              file: file.file,
            })
          );
          succeeded.push(file);
          succeededFiles.push({
            file_id: reservation.file_id,
            filename: reservation.filename,
          });
        } catch {
          failed.push(file);
        }
      }

      if (failed.length && succeeded.length) {
        message({
          type: 'warning',
          text: `Загружено ${succeeded.length} из ${files.length} файлов`,
        });
      } else if (failed.length) {
        message({
          type: 'error',
          text: 'Не удалось загрузить файлы',
        });
      }

      if (succeededFiles.length) {
        setStatus('attaching');

        try {
          await dispatch(
            attachFilesToPublicRequest({
              id: publicId,
              files: succeededFiles,
            })
          ).then(unwrapResult);
        } catch {
          message({
            type: 'error',
            text: 'Не удалось прикрепить файлы к заявке',
          });
          setStatus('error');

          return { succeeded: [], failed: files };
        }
      }

      setStatus(failed.length === files.length ? 'error' : 'done');

      return { succeeded, failed };
    },
    [dispatch, publicId]
  );

  const reset = useCallback(() => {
    setStatus('idle');
  }, []);

  return { upload, status, reset };
};
